import * as React from "react";
import { useEffect, useState } from "react";
import isEmpty from "lodash/isEmpty";
import { useStore } from "stores/connect";
import { EntityShareStore } from "stores/permissions/EntityShareStore";
import EntityShareDomain from "domainActions/permissions/EntityShareDomain";
import { createGRN } from "logic/permissions/GRN";
import { Section, Spinner } from "components/common";
import usePluggableEntityShareFormGroup from "hooks/usePluggableEntityShareFormGroup";
import GranteesList from "components/permissions/Grantee/GranteesList";
import EntityCreateCapabilitySelect from "./EntityCreateCapabilitySelect";
import {
  GranteesSelect,
  GranteesSelectOption,
  GranteesSelectorHeadline,
  ShareFormElements,
  ShareFormSection,
  ShareSubmitButton,
  StyledGranteeIcon
} from "./CommonStyledComponents";
import EntityShareValidationsDependencies from "./EntityShareValidationsDependencies";
const _renderGranteesSelectOption = ({ label, granteeType }) => /* @__PURE__ */ React.createElement(GranteesSelectOption, null, /* @__PURE__ */ React.createElement(StyledGranteeIcon, { type: granteeType }), label);
const _granteesOptions = (grantees) => grantees.map((grantee) => ({ label: grantee.title, value: grantee.id, granteeType: grantee.type })).toJS();
const getAvailableGrantee = (grantees, selected) => {
  var _a;
  return (_a = grantees == null ? void 0 : grantees.filter((g) => !selected.has(g.id))) == null ? void 0 : _a.toList();
};
const EntityCreateShareFormGroup = ({
  description,
  entityType,
  entityTitle = "",
  onSetEntityShare,
  entityId = null,
  entityTypeTitle = "",
  dependenciesGRN = null,
  defaultSharePayload = void 0
}) => {
  var _a;
  const { state: entityShareState } = useStore(EntityShareStore);
  const entityGRN = entityId && createGRN(entityType, entityId);
  const defaultShareSelection = { granteeId: null, capabilityId: "view" };
  const [disableSubmit, setDisableSubmit] = useState((_a = entityShareState == null ? void 0 : entityShareState.validationResults) == null ? void 0 : _a.failed);
  const [shareSelection, setShareSelection] = useState(defaultShareSelection);
  const [entityShare, setEntityShare] = useState(
    defaultSharePayload != null ? defaultSharePayload : null
  );
  const PluggableEntityShareFormGroup = usePluggableEntityShareFormGroup();
  useEffect(() => {
    const { selected_collections: _, ...rest } = defaultSharePayload != null ? defaultSharePayload : {};
    EntityShareDomain.prepare(entityType, entityTitle, entityGRN, rest);
  }, [entityType, entityTitle, entityGRN, defaultSharePayload]);
  const resetSelection = () => {
    setDisableSubmit(false);
    setShareSelection(defaultShareSelection);
  };
  const handleSelection = ({ granteeId, capabilityId }) => {
    const newSelectedCapabilities = entityShareState == null ? void 0 : entityShareState.selectedGranteeCapabilities.merge({ [granteeId]: capabilityId });
    setDisableSubmit(true);
    const payload = {
      selected_grantee_capabilities: newSelectedCapabilities,
      prepare_request: dependenciesGRN
    };
    setEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
    return EntityShareDomain.prepare(entityType, entityTitle, entityGRN, payload).then((response) => {
      onSetEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
      resetSelection();
      return response;
    });
  };
  const handleDeletion = (granteeId) => {
    const newSelectedCapabilities = entityShareState == null ? void 0 : entityShareState.selectedGranteeCapabilities.remove(granteeId);
    setDisableSubmit(true);
    const prepare_request = isEmpty(newSelectedCapabilities) ? null : dependenciesGRN;
    const payload = {
      selected_grantee_capabilities: newSelectedCapabilities,
      prepare_request
    };
    setEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
    return EntityShareDomain.prepare(entityType, entityTitle, null, payload).then((response) => {
      onSetEntityShare({ ...entityShare, selected_grantee_capabilities: newSelectedCapabilities });
      setDisableSubmit(false);
      return response;
    });
  };
  const handleAdditionalFormChange = (values) => {
    const newEntityShare = { ...entityShare, ...values };
    setEntityShare(newEntityShare);
    onSetEntityShare(newEntityShare);
  };
  const handleAddCollaborator = () => {
    handleSelection(shareSelection);
  };
  return /* @__PURE__ */ React.createElement(Section, { title: "" }, entityShareState ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ShareFormSection, null, /* @__PURE__ */ React.createElement(GranteesSelectorHeadline, null, "Add Collaborator"), /* @__PURE__ */ React.createElement("p", null, description), /* @__PURE__ */ React.createElement(ShareFormElements, null, /* @__PURE__ */ React.createElement(
    GranteesSelect,
    {
      onChange: (granteeId) => setShareSelection({ ...shareSelection, granteeId }),
      optionRenderer: _renderGranteesSelectOption,
      options: _granteesOptions(
        getAvailableGrantee(entityShareState.availableGrantees, entityShareState.selectedGranteeCapabilities)
      ),
      placeholder: "Search for users and teams",
      value: shareSelection.granteeId
    }
  ), /* @__PURE__ */ React.createElement(
    EntityCreateCapabilitySelect,
    {
      onChange: (capabilityId) => setShareSelection({ ...shareSelection, capabilityId }),
      capabilities: entityShareState == null ? void 0 : entityShareState.availableCapabilities,
      value: shareSelection.capabilityId
    }
  ), /* @__PURE__ */ React.createElement(
    ShareSubmitButton,
    {
      bsStyle: "primary",
      title: "Add Collaborator",
      onClick: handleAddCollaborator,
      disabled: disableSubmit || !shareSelection.granteeId
    },
    "Add Collaborator"
  ))), /* @__PURE__ */ React.createElement(ShareFormSection, null, /* @__PURE__ */ React.createElement(
    GranteesList,
    {
      activeShares: entityShareState == null ? void 0 : entityShareState.activeShares,
      availableCapabilities: entityShareState == null ? void 0 : entityShareState.availableCapabilities,
      entityType,
      entityTypeTitle,
      onDelete: handleDeletion,
      onCapabilityChange: handleSelection,
      selectedGrantees: entityShareState == null ? void 0 : entityShareState.selectedGrantees,
      title: "Collaborators",
      isCreating: true
    }
  )), /* @__PURE__ */ React.createElement(
    EntityShareValidationsDependencies,
    {
      missingDependencies: entityShareState.missingDependencies,
      validationResults: entityShareState.validationResults,
      availableGrantees: entityShareState.availableGrantees
    }
  ), PluggableEntityShareFormGroup && /* @__PURE__ */ React.createElement(
    PluggableEntityShareFormGroup,
    {
      entityType,
      onChange: handleAdditionalFormChange,
      value: (defaultSharePayload == null ? void 0 : defaultSharePayload.selected_collections) || []
    }
  )) : /* @__PURE__ */ React.createElement(Spinner, null));
};
export default EntityCreateShareFormGroup;
